import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { useDroppable } from '@dnd-kit/core'
import {
  EmptyState,
  FileTextIcon,
  Skeleton,
  SkeletonText,
  useMediaQuery,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { CalendarUndatedDraftDragSource } from '~publish/components/Calendar/CalendarUndatedDraftDragSource'
import { GuestDraftImportNotice } from '~publish/components/GuestDraftHandoff'
import {
  CalendarUndatedDraftMiniCard,
  CalendarUndatedDraftMiniTrigger,
  CalendarUndatedDraftsList,
  CalendarUndatedDraftsPanel,
  useUndatedDraftsPanelKeyboard,
} from '~publish/components/Calendar/CalendarUndatedDraftsPanel'
import type { PostStatus } from '~publish/components/graphqlEnums'
import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { postStatusByTab } from '~publish/helpers/post'
import { useChannelAgnosticDraftsEnabled } from '~publish/hooks/useChannelAgnosticDraftsEnabled'
import { PostListError } from '~publish/pages/AllChannels/PostList/PostListError'
import { usePaginatedPostList } from '~publish/pages/AllChannels/PostList/usePaginatedPostList'

import { CalendarUndatedContentItemDraft } from './CalendarUndatedContentItemDraft'
import { CalendarPostPopover } from './CalendarPostPopover'
import { CalendarPostCard_Post } from './fragments/CalendarPostCard_Post'
import { PostMiniature } from './PostMiniature'
import {
  isUndatedDraftDragData,
  UNDATED_DRAFT_DRAG_SOURCE,
  UNDATED_DRAFTS_SIDEBAR_DROP_TARGET,
} from './undatedDraftDrag'
import type { UndatedDraftDragData } from './undatedDraftDrag'
import { useUndatedContentItemDrafts } from './useUndatedContentItemDrafts'

import styles from './CalendarPage.module.css'

const UNDATED_DRAFT_STATUSES: PostStatus[] = [
  ...postStatusByTab.drafts,
  ...postStatusByTab.approvals,
]

const SKELETON_CARDS = [0, 1, 2]
const CALENDAR_HIDDEN_MEDIA_QUERY = '(width <= 1024px)'

type UndatedDraftSidebarPost = FragmentOf<typeof CalendarPostCard_Post> & {
  id: string
}

function isUndatedDraftSidebarPost(
  post: unknown,
): post is UndatedDraftSidebarPost {
  // The sidebar query selects CalendarPostCard_Post; this guard only filters
  // out unusable partial edges while Apollo fills missing non-sidebar fields.
  return (
    typeof post === 'object' &&
    post !== null &&
    'id' in post &&
    typeof post.id === 'string'
  )
}

const CalendarUndatedDraftMiniSkeletonCard = (): JSX.Element => (
  <CalendarUndatedDraftMiniCard>
    <div className={styles.undatedDraftMiniSkeleton}>
      <div className={styles.undatedDraftMiniSkeletonHeader}>
        <Skeleton rounded width={24} height={24} />
        <SkeletonText lines={1} width={120} />
      </div>
      <SkeletonText lines={2} width="100%" />
      <div className={styles.undatedDraftMiniSkeletonTags}>
        <Skeleton height={6} width={56} />
        <Skeleton height={6} width={40} />
      </div>
    </div>
  </CalendarUndatedDraftMiniCard>
)

const CalendarUndatedDraftsSkeleton = (): JSX.Element => {
  return (
    <CalendarUndatedDraftsList aria-busy>
      <VisuallyHidden>Loading undated drafts</VisuallyHidden>
      {SKELETON_CARDS.map((id) => (
        <CalendarUndatedDraftMiniSkeletonCard key={id} />
      ))}
    </CalendarUndatedDraftsList>
  )
}

const CalendarUndatedDraftPost = ({
  isDragDisabled,
  isSidebarOpen,
  post,
}: {
  isDragDisabled: boolean
  isSidebarOpen: boolean
  post: FragmentOf<typeof CalendarPostCard_Post>
}): JSX.Element => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const postData = readFragment(CalendarPostCard_Post, post)
  const postType = postData.status === 'needs_approval' ? 'approval' : 'draft'
  const undatedDraftLabel = `Open undated ${postType} for ${postData.channel.name}`
  const dragLabel = `Drag undated ${postType} for ${postData.channel.name} to schedule`
  const dragData: UndatedDraftDragData = {
    type: 'post',
    source: UNDATED_DRAFT_DRAG_SOURCE,
    post: postData,
    label: `undated ${postType} for ${postData.channel.name}`,
  }
  useEffect(() => {
    if (!isSidebarOpen) {
      setIsPreviewOpen(false)
    }
  }, [isSidebarOpen])

  return (
    <CalendarUndatedDraftDragSource
      id={`undated:${postData.id}`}
      data={dragData}
      disabled={isDragDisabled}
      dragLabel={dragLabel}
    >
      <CalendarPostPopover
        post={post}
        isOpen={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        side="left"
      >
        <CalendarUndatedDraftMiniTrigger aria-label={undatedDraftLabel}>
          <PostMiniature
            post={postData}
            className={styles.undatedDraftMiniature}
            statusBadgePlacement="header"
          />
        </CalendarUndatedDraftMiniTrigger>
      </CalendarPostPopover>
    </CalendarUndatedDraftDragSource>
  )
}

type CalendarUndatedDraftsSidebarProps = {
  id: string
  isOpen: boolean
  isSchedulingDisabled?: boolean
  channelIds?: string[]
  tagIds?: string[]
  onClose: () => void
}

export const CalendarUndatedDraftsSidebar = ({
  id,
  isOpen,
  isSchedulingDisabled = false,
  channelIds,
  tagIds,
  onClose,
}: CalendarUndatedDraftsSidebarProps): JSX.Element => {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const sidebarRef = useRef<HTMLElement | null>(null)
  // The calendar pane is hidden at the same breakpoint in
  // CalendarPage.module.css, so there is no valid scheduling drop target.
  const isCalendarHidden = useMediaQuery(CALENDAR_HIDDEN_MEDIA_QUERY)
  const isDragDisabled = isSchedulingDisabled || isCalendarHidden
  const {
    active,
    isOver,
    setNodeRef: setDroppableNodeRef,
  } = useDroppable({
    id,
    disabled: !isOpen || isDragDisabled,
    data: {
      type: UNDATED_DRAFTS_SIDEBAR_DROP_TARGET,
      label: 'undated drafts',
    },
  })
  const { data, loading, error, lastElementRef, fetchingMore } =
    usePaginatedPostList({
      status: UNDATED_DRAFT_STATUSES,
      channelIds,
      tagIds,
      dueAtPresence: 'absent',
      returnPartialData: true,
    })

  const posts =
    data?.posts?.edges
      ?.map((edge) => edge?.node)
      .filter(isUndatedDraftSidebarPost) ?? []
  const lastPostId = posts.at(-1)?.id

  // Channel-less drafts belong to no channel, so surface them only in the
  // all-channels sidebar — not when the calendar is filtered to a channel.
  const channelAgnosticDraftsEnabled = useChannelAgnosticDraftsEnabled()
  const showContentItemDrafts =
    channelAgnosticDraftsEnabled && (channelIds?.length ?? 0) === 0
  const {
    drafts: fetchedContentItemDrafts,
    loading: contentItemDraftsLoading,
    error: contentItemDraftsError,
    fetchingMore: contentItemDraftsFetchingMore,
    lastElementRef: contentItemLastRef,
  } = useUndatedContentItemDrafts({ skip: !showContentItemDrafts })
  // Gate the rendered list on the current filter state, not just the query's
  // `skip`: a skipped Apollo query keeps handing back the drafts it fetched in
  // a previous (all-channels) render, which would otherwise leak channel-less
  // items into a channel-filtered sidebar.
  const contentItemDrafts = showContentItemDrafts
    ? fetchedContentItemDrafts
    : []
  const lastContentItemDraftId = contentItemDrafts.at(-1)?.id
  const sidebarError = error ?? contentItemDraftsError

  const showLoadingState =
    (loading && posts.length === 0 && !sidebarError) ||
    (showContentItemDrafts &&
      contentItemDraftsLoading &&
      contentItemDrafts.length === 0)
  const isEmpty = posts.length === 0 && contentItemDrafts.length === 0
  const showEmptyState =
    !showLoadingState &&
    !(typeof data === 'undefined' && !sidebarError) &&
    !sidebarError &&
    isEmpty
  const setSidebarNodeRef = useCallback(
    (node: HTMLElement | null): void => {
      sidebarRef.current = node
      setDroppableNodeRef(node)
    },
    [setDroppableNodeRef],
  )
  const isDraggingScheduledPost =
    active?.data.current?.type === 'post' &&
    !isUndatedDraftDragData(active.data.current)
  const isRemovingScheduledTime = isOver && isDraggingScheduledPost

  useUndatedDraftsPanelKeyboard({
    isOpen,
    panelRef: sidebarRef,
    closeButtonRef,
    onClose,
  })

  return (
    <CalendarUndatedDraftsPanel
      ref={setSidebarNodeRef}
      id={id}
      className={styles.undatedDraftsSidebar}
      closeButtonRef={closeButtonRef}
      isEmpty={showEmptyState}
      isDropTarget={isRemovingScheduledTime}
      onClose={onClose}
      hidden={!isOpen}
    >
      <div className={styles.guestDraftImportNotice}>
        <GuestDraftImportNotice />
      </div>
      {showLoadingState || (typeof data === 'undefined' && !sidebarError) ? (
        <CalendarUndatedDraftsSkeleton />
      ) : sidebarError ? (
        <PostListError error={sidebarError} size="medium" />
      ) : isEmpty ? (
        <EmptyState size="small">
          <EmptyState.Icon>
            <FileTextIcon />
          </EmptyState.Icon>
          <div id={`${id}-heading`}>
            <EmptyState.Heading as="h2">No Undated Drafts</EmptyState.Heading>
          </div>
          <EmptyState.Description id={`${id}-description`}>
            Drafts and approvals without a scheduled date will appear here.
          </EmptyState.Description>
        </EmptyState>
      ) : (
        <CalendarUndatedDraftsList
          role="feed"
          aria-label="Undated draft posts"
          aria-busy={fetchingMore || contentItemDraftsFetchingMore}
        >
          {/* Channel-less drafts lead — they have no channel to sort by. */}
          {contentItemDrafts.map((contentItem) => (
            <CalendarUndatedDraftMiniCard
              key={contentItem.id}
              ref={
                contentItem.id === lastContentItemDraftId
                  ? contentItemLastRef
                  : null
              }
            >
              <CalendarUndatedContentItemDraft
                contentItem={contentItem}
                isDragDisabled={isDragDisabled}
                isSidebarOpen={isOpen}
              />
            </CalendarUndatedDraftMiniCard>
          ))}
          {contentItemDraftsFetchingMore && (
            <CalendarUndatedDraftMiniSkeletonCard />
          )}
          {posts.map((post) => (
            <CalendarUndatedDraftMiniCard
              key={post.id}
              ref={post.id === lastPostId ? lastElementRef : null}
            >
              <CalendarUndatedDraftPost
                isDragDisabled={isDragDisabled}
                isSidebarOpen={isOpen}
                post={post}
              />
            </CalendarUndatedDraftMiniCard>
          ))}
          {fetchingMore && <CalendarUndatedDraftMiniSkeletonCard />}
        </CalendarUndatedDraftsList>
      )}
    </CalendarUndatedDraftsPanel>
  )
}
